import { useCallback, useState } from "react"

import { useRecoilValue } from "recoil"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import type { Contract } from "~/graphql/client"
import {
  useGetContractVersionHistoryForProposalQuery,
  useGetContractsForProposalQuery,
  useRequestContractMutation,
  useResendContractMutation,
  useSendSupplementalContractMutation,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"

import { proposalLocalState } from "./proposal/proposal-state"
import { useProposalStatuses } from "./proposal/status"

export interface ContractVersionHistoryItem {
  versionNumber: number
  isVoid: boolean
  isSigned: boolean
  systemSize: number | null
  totalCost: number | null
  sentAt: string | null
  recipientEmail: string | null
}

export type UseContracts = () => {
  contracts: Contract[]
  versionHistory: ContractVersionHistoryItem[]
  /** Re-read the packages after something outside this hook changes them. */
  refetchContracts: () => Promise<void>
  requestContract: () => Promise<void>
  resendContract: (contractId: string) => Promise<void>
  sendSupplementalContract: (contractId: string) => Promise<void>
  loading: boolean
  resendingContractId: string | null
  sendingSupplementalId: string | null
  resendSuccess: string | null
  sendSupplementalSuccess: string | null
  clearResendSuccess: () => void
  clearSendSupplementalSuccess: () => void
}

export const useContracts: UseContracts = () => {
  const { id: proposalId } = useRecoilValue(proposalLocalState)
  const { refetch: getStatuses } = useProposalStatuses()
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const [resendingContractId, setResendingContractId] = useState<string | null>(
    null,
  )
  const [resendSuccess, setResendSuccess] = useState<string | null>(null)
  const [sendingSupplementalId, setSendingSupplementalId] = useState<
    string | null
  >(null)
  const [sendSupplementalSuccess, setSendSupplementalSuccess] = useState<
    string | null
  >(null)

  const {
    data,
    refetch: getContracts,
    isLoading: getContractsLoading,
  } = useGetContractsForProposalQuery(
    client,
    {
      proposalId,
    },
    {
      enabled: !!proposalId,
      onError,
    },
  )

  // Use data directly from React Query cache instead of local state
  const contracts = data?.contractsForProposal ?? []

  const {
    data: versionHistoryData,
    refetch: getContractVersionHistory,
    isLoading: getContractVersionHistoryLoading,
  } = useGetContractVersionHistoryForProposalQuery(
    client,
    {
      proposalId,
    },
    {
      enabled: !!proposalId,
      onError,
    },
  )

  const versionHistory =
    versionHistoryData?.contractVersionHistoryForProposal ?? []

  const { isLoading: mutationInFlight, mutateAsync } =
    useRequestContractMutation(client, {
      onError,
      onSuccess: async (data) => {
        if (data.updateStatusProposalContractRequested) {
          await Promise.all([
            getStatuses(),
            getContracts(),
            getContractVersionHistory(),
          ])
        }
      },
    })

  const { mutateAsync: resendMutateAsync } = useResendContractMutation(client, {
    onError,
    onSuccess: async () => {
      await Promise.all([getContracts(), getContractVersionHistory()])
    },
  })

  const { mutateAsync: sendSupplementalMutateAsync } =
    useSendSupplementalContractMutation(client, {
      onError,
      onSuccess: async () => {
        await Promise.all([getContracts(), getContractVersionHistory()])
      },
    })

  const requestContract = useCallback(async () => {
    if (mutationInFlight) {
      console.warn(`Cannot send while in flight`)

      return
    }
    await mutateAsync({ data: { proposalId, message: `Requesting a Contract` } })
    await Promise.all([getContracts(), getContractVersionHistory()])
  }, [
    mutationInFlight,
    proposalId,
    mutateAsync,
    getContracts,
    getContractVersionHistory,
  ])

  const resendContract = useCallback(
    async (contractId: string) => {
      if (resendingContractId) {
        console.warn(`Cannot resend while another resend is in flight`)

        return
      }
      setResendSuccess(null)
      setResendingContractId(contractId)
      try {
        await resendMutateAsync({ id: contractId })
        setResendSuccess(contractId)
        // Auto-clear success message after 5 seconds
        setTimeout(() => setResendSuccess(null), 5000)
      } finally {
        setResendingContractId(null)
      }
    },
    [resendingContractId, resendMutateAsync],
  )

  const clearResendSuccess = useCallback(() => {
    setResendSuccess(null)
  }, [])

  const sendSupplementalContract = useCallback(
    async (contractId: string) => {
      if (sendingSupplementalId) {
        console.warn(`Cannot send while another send is in flight`)

        return
      }
      setSendSupplementalSuccess(null)
      setSendingSupplementalId(contractId)
      try {
        await sendSupplementalMutateAsync({ id: contractId })
        setSendSupplementalSuccess(contractId)
        // Auto-clear success message after 5 seconds
        setTimeout(() => setSendSupplementalSuccess(null), 5000)
      } finally {
        setSendingSupplementalId(null)
      }
    },
    [sendingSupplementalId, sendSupplementalMutateAsync],
  )

  const clearSendSupplementalSuccess = useCallback(() => {
    setSendSupplementalSuccess(null)
  }, [])

  return {
    contracts,
    versionHistory,
    /** Re-read the packages after something outside this hook changes them. */
    refetchContracts: async (): Promise<void> => {
      await Promise.all([getContracts(), getContractVersionHistory()])
    },
    requestContract,
    resendContract,
    sendSupplementalContract,
    loading:
      mutationInFlight ||
      getContractsLoading ||
      getContractVersionHistoryLoading,
    resendingContractId,
    sendingSupplementalId,
    resendSuccess,
    sendSupplementalSuccess,
    clearResendSuccess,
    clearSendSupplementalSuccess,
  }
}
